import React, { useEffect, useState } from "react";
import Title from "../../components/common/Title";
import { Topbar } from "../../components/include/Topbar";
import { Postdata } from "../../helpers/api.service";
import HttpService from "../../helpers/httpServices";
import { useSelector } from "react-redux";
import Page from "../../helpers/config";
import Pagination from "../../components/common/Pagination";
import DeleteModal from "../../components/common/DeleteModal";
import { isPermission, isRole, isRoleType } from "../../redux/selectors/authSelector";
import Error403Page from "../ErrorPage";
import { useCountryNavigate } from "../../components/common/UseCountryNavigate";
import { toast } from "sonner";
import axios from "axios";

const statusOptions = [
  { label: "All", value: "" },
  { label: "Pending", value: "pending" },
  { label: "Sent", value: "sent" },
  { label: "Partially Sent", value: "partially_sent" },
  { label: "Failed", value: "failed" },
  { label: "Cancelled", value: "cancelled" },
];

const getStatusBadge = (status) => {
  const map = {
    pending: "bg-yellow-100 text-yellow-800",
    sent: "bg-green-100 text-green-800",
    partially_sent: "bg-orange-100 text-orange-800",
    failed: "bg-red-100 text-red-800",
    cancelled: "bg-gray-100 text-gray-800",
  };
  return map[status] || "bg-gray-100 text-gray-600";
};

const CommunicationList = ({ schoolId: propSchoolId, currentTab }) => {
  const [data, setData] = useState([]);
  const navigate = useCountryNavigate();
  const [loader, setLoader] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(Page.records);
  const [currentPage, setCurrentPage] = useState(1);
  const [modalOpenDelete, setModalOpenDelete] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [modalOpenDeleteSuccess, setModalOpenDeleteSuccess] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [hasFetched, setHasFetched] = useState(false);
  const [credit, setCredit] = useState(null);

  const permissions = useSelector(isPermission);
  const role = useSelector(isRole);
  const role_type = useSelector(isRoleType);
  const mode = localStorage.getItem("mode");
  const schoolId = propSchoolId || localStorage.getItem("activeSchoolId");

  const isAccessible =
    role === "admin" ||
    role === "subadmin" ||
    role === "schooladmin" ||
    (permissions &&
      (permissions?.includes("view_Communication") ||
        permissions?.includes("view_School")));

  const isEditable =
    role === "subadmin" ||
    role === "schooladmin" ||
    (permissions &&
      (permissions?.includes("edit_Communication") ||
        permissions?.includes("edit_School")));

  const isDeletable =
    role === "subadmin" ||
    role === "schooladmin" ||
    (permissions &&
      (permissions?.includes("delete_Communication") ||
        permissions?.includes("delete_School")));

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (rows) => {
    setPerPage(rows);
    setCurrentPage(1);
  };

  // Delete handler
  const handleDelete = async (id) => {
    try {
      const res = await Postdata(HttpService.communication_delete, { id });
      if (res.status === 200) {
        toast.success("Communication deleted successfully");
        fetchData();
      } else {
        toast.error(res.message || "Failed to delete communication");
      }
    } catch (error) {
      toast.error("Failed to delete communication");
    }
  };

  // Fetch communications
  const fetchData = async () => {
    setLoader(true);
    try {
      const res = await Postdata(HttpService.communication_list, {
        page: currentPage,
        perpage: perPage,
        search: searchQuery,
        status: statusFilter,
        school_id: schoolId,
      });

      if (res.status === 200) {
        setData(res.data?.communications || []);
        setTotalRows(res.data?.count || 0);
        setCredit(res.data?.credit ?? null);
        setHasFetched(true);
      }
    } catch (error) {
      console.error("Error fetching communications", error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, perPage, searchQuery, statusFilter]);

  if (!isAccessible) {
    return <Error403Page />;
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const truncateMessage = (msg, len = 15) => {
    if (!msg) return "-";
    return msg.length > len ? msg.substring(0, len) + "..." : msg;
  };

  const handleCommunicationNavigation = (path, state) => {
    if (
      role === "admin" ||
      role === "subadmin" ||
      role_type === "schoolsubadmin" ||
      role_type === "subadmin"
    ) {
      if (mode === "view") {
        navigate(path, { state });
      } else {
        navigate(path, { state });
      }
    } else {
      navigate(path, { state });
    }
  };

  return (
    <>
      {!propSchoolId && <Title name="Communication" />}
      {!propSchoolId && <Topbar title="Communication" />}
      <div className="p-6 space-y-6 pr-0">
        {/* Credit Badge & Search & Filters */}
        <div className="flex flex-wrap gap-3 justify-between items-center">
          {credit !== null && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-blue-700">SMS Credits:</span>
                <span className={`text-lg font-bold ${credit <= 10 ? 'text-red-600' : 'text-blue-800'}`}>{credit}</span>
              </div>
              {credit === 0 && (
                <span className="text-red-500" style={{ fontSize: '10px' }}>Contact super admin for credits.</span>
              )}
            </div>
          )}
          <div className="flex flex-wrap gap-3 items-center ml-auto">
          <input
            type="text"
            placeholder="Search by message..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:outline-none w-72"
          />
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
          >
            {statusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {isEditable && (
            <button
              className={`bg-primary_main text-white px-4 py-2 rounded hover:bg-primary_dark ${credit === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => navigate("communication/add", { state: { credit } })}
              disabled={credit === 0}
              title={credit === 0 ? "No SMS credits available" : ""}
            >
              + New Message
            </button>
          )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded overflow-x-auto p-2">
          <table className="min-w-full text-sm text-left">
            <thead className="text-gray-700">
              <tr>
                <th className="px-4 py-3 font-medium">#</th>
                <th className="px-4 py-3 font-medium">Message</th>
                <th className="px-4 py-3 font-medium">Recipient</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Target</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Recipients</th>
                <th className="px-4 py-3 font-medium">Credits</th>
                <th className="px-4 py-3 font-medium">Scheduled</th>
                <th className="px-4 py-3 font-medium">Created</th>
                <th className="px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loader || !hasFetched ? (
                <tr>
                  <td colSpan="11" className="text-center py-4">
                    <div className="loader">Loading...</div>
                  </td>
                </tr>
              ) : (
                data.map((item, index) => (
                  <tr key={item._id || index} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      {(currentPage - 1) * perPage + index + 1}
                    </td>
                    <td className="px-4 py-4" title={item.message}>
                      {truncateMessage(item.message)}
                    </td>
                    <td className="px-4 py-4 capitalize">
                      {item.recipient_module === "school_management" ? "School Management" : item.recipient_module === "board" ? "Board" : item.recipient_module === "teacher" ? "Teacher" : item.recipient_module === "parent" ? "Parents" : item.recipient_module || "-"}
                    </td>
                    <td className="px-4 py-4 capitalize">
                      {item.type || "-"}
                    </td>
                    <td className="px-4 py-4 capitalize">
                      {item.target === "all"
                        ? "All"
                        : item.target === "selected"
                        ? "Selected"
                        : item.target || "-"}
                    </td>
                    <td className="px-4 py-4">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusBadge(
                          item.status
                        )}`}
                      >
                        {item.status?.replace("_", " ") || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      {item.total_recipients ?? "-"}
                    </td>
                    <td className="px-4 py-4">
                      {item.credits_used ?? "-"}
                    </td>
                    <td className="px-4 py-4">
                      {formatDate(item.scheduled_at)}
                    </td>
                    <td className="px-4 py-4">
                      {formatDate(item.createdAt)}
                    </td>
                    <td className="px-4 py-4 space-x-3">
                      <button
                        className="text-black hover:underline"
                        onClick={() =>
                          navigate(`communication/view/${item._id}`)
                        }
                      >
                        View
                      </button>
                      {isEditable && item.status === "pending" && (
                        <button
                          className="text-primary_main hover:underline"
                          onClick={() =>
                            navigate(`communication/edit/${item._id}`)
                          }
                        >
                          Edit
                        </button>
                      )}
                      {isDeletable &&
                        (item.status === "pending" ||
                          item.status === "cancelled") && (
                          <button
                            className="text-red-600 hover:underline"
                            onClick={() => {
                              setSelectedId(item._id);
                              setModalOpenDelete(true);
                            }}
                          >
                            Delete
                          </button>
                        )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {!loader && data !== null && hasFetched && data.length === 0 && (
            <div className="h-[50vh] flex items-center justify-center w-full">
              <h1 className="text-center text-gray-400">No Data Found</h1>
            </div>
          )}
        </div>

        <Pagination
          currentPage={currentPage}
          totalRows={totalRows}
          perPage={perPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          type=""
        />

        <DeleteModal
          title="Delete Communication"
          message="Are you sure you want to delete this communication? This action cannot be undone."
          modalOpenDelete={modalOpenDelete}
          setmodalOpenDelete={setModalOpenDelete}
          handleDelete={handleDelete}
          selectedId={selectedId}
          setSelectedId={setSelectedId}
          modalOpenDeleteSuccess={modalOpenDeleteSuccess}
          setModalOpenDeleteSuccess={setModalOpenDeleteSuccess}
          module={"Communication"}
          redirectPage={"Communication"}
          redirectUrl={"communication"}
        />
      </div>
    </>
  );
};

export default CommunicationList;
